import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import UserBadge from '../components/UserBadge';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../config/api';

const NEWS_CATEGORIES = ['Forschung', 'System'];

interface NewsEntry {
  _id: string;
  title: string;
  content: string;
  category: string;
  image?: string;
  authorid: string;
  publishedAt: string;
  createdAt: string;
}

const News: React.FC = () => {
  const navigate = useNavigate();
  const { isStaff } = useAuth();
  const [news, setNews] = useState<NewsEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const todayDate = () => new Date().toISOString().slice(0, 10);
  const currentTime = () => new Date().toTimeString().slice(0, 5);

  const [showForm, setShowForm] = useState(false);
  const [formTitle, setFormTitle] = useState('');
  const [formContent, setFormContent] = useState('');
  const [formCategory, setFormCategory] = useState(NEWS_CATEGORIES[0]);
  const [formDate, setFormDate] = useState(todayDate);
  const [formTime, setFormTime] = useState(currentTime);
  const [formImage, setFormImage] = useState<File | null>(null);
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await apiRequest('/news');
        if (!response.ok) throw new Error('Fehler beim Laden');
        const data = await response.json();
        setNews(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Fehler beim Laden');
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormSaving(true);
    setFormError(null);
    try {
      const body = new FormData();
      body.append('title', formTitle);
      body.append('content', formContent);
      body.append('category', formCategory);
      body.append('publishedAt', new Date(`${formDate}T${formTime}`).toISOString());
      if (formImage) body.append('image', formImage);

      const response = await apiRequest('/news', { method: 'POST', body });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Fehler beim Speichern');
      }
      const created = await response.json();
      setNews([created, ...news]);
      setShowForm(false);
      setFormTitle('');
      setFormContent('');
      setFormCategory(NEWS_CATEGORIES[0]);
      setFormDate(todayDate());
      setFormTime(currentTime());
      setFormImage(null);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Fehler beim Speichern');
    } finally {
      setFormSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <LoadingSpinner message="Lade Meldungen..." fullPage />
      </div>
    );
  }

  return (
    <div className="container" style={{ padding: '40px' }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 style={{ color: '#687088', fontWeight: 500, margin: 0 }}>Aktuelle Meldungen</h3>
        <div className="d-flex align-items-center gap-3">
          {isStaff && (
            <button
              className="btn btn-primary btn-sm"
              style={{ fontWeight: 700 }}
              onClick={() => setShowForm(true)}
            >
              + Neue Meldung
            </button>
          )}
          <UserBadge />
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* News list */}
      {news.length === 0 && !error && (
        <p style={{ color: '#687088' }}>Keine Meldungen vorhanden.</p>
      )}
      {news.map((entry) => (
        <div key={entry._id} className="card shadow-sm mb-3" style={{ borderRadius: '12px' }}>
          <div className="card-body p-4 d-flex justify-content-between align-items-center gap-4">
            <div style={{ flex: 1, minWidth: 0 }}>
              <h6
                onClick={() => navigate(`/news/${entry._id}`)}
                style={{ color: '#0d6efd', fontWeight: 700, marginBottom: '4px', cursor: 'pointer' }}
              >
                {entry.title}
              </h6>
              <p style={{ color: '#687088', fontSize: '14px', marginBottom: '4px', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                {entry.content}
              </p>
              <small style={{ color: '#a0afc0', fontSize: '12px' }}>
                {formatDate(entry.publishedAt || entry.createdAt)} · Kategorie {entry.category}
              </small>
            </div>
            <button
              className="btn btn-primary"
              style={{ fontWeight: 700, fontSize: '13px', whiteSpace: 'nowrap', flexShrink: 0 }}
              onClick={() => navigate(`/news/${entry._id}`)}
            >
              Details
            </button>
          </div>
        </div>
      ))}

      {/* Create modal (staff only) */}
      {showForm && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => setShowForm(false)}
        >
          <div
            className="modal-dialog modal-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content" style={{ borderRadius: '12px' }}>
              <div className="modal-header" style={{ borderBottom: '1px solid #e0e4ef' }}>
                <h5 className="modal-title" style={{ color: '#687088', fontWeight: 700 }}>
                  Neue Meldung
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowForm(false)}
                />
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  {formError && <div className="alert alert-danger">{formError}</div>}
                  <div className="mb-3">
                    <label className="form-label" style={{ color: '#687088', fontWeight: 600, fontSize: '14px' }}>
                      Titel
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      required
                      value={formTitle}
                      onChange={(e) => setFormTitle(e.target.value)}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label" style={{ color: '#687088', fontWeight: 600, fontSize: '14px' }}>
                      Kategorie
                    </label>
                    <select
                      className="form-select"
                      value={formCategory}
                      onChange={(e) => setFormCategory(e.target.value)}
                    >
                      {NEWS_CATEGORIES.map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label" style={{ color: '#687088', fontWeight: 600, fontSize: '14px' }}>
                      Datum &amp; Uhrzeit
                    </label>
                    <div className="d-flex gap-2">
                      <input
                        type="date"
                        className="form-control"
                        required
                        value={formDate}
                        onChange={(e) => setFormDate(e.target.value)}
                      />
                      <input
                        type="time"
                        className="form-control"
                        required
                        value={formTime}
                        onChange={(e) => setFormTime(e.target.value)}
                        style={{ maxWidth: '140px' }}
                      />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label" style={{ color: '#687088', fontWeight: 600, fontSize: '14px' }}>
                      Text
                    </label>
                    <textarea
                      className="form-control"
                      rows={6}
                      required
                      style={{ resize: 'vertical' }}
                      value={formContent}
                      onChange={(e) => setFormContent(e.target.value)}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label" style={{ color: '#687088', fontWeight: 600, fontSize: '14px' }}>
                      Bild (optional)
                    </label>
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={(e) => setFormImage(e.target.files?.[0] || null)}
                    />
                  </div>
                </div>
                <div className="modal-footer" style={{ borderTop: '1px solid #e0e4ef' }}>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowForm(false)}
                  >
                    Abbrechen
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    style={{ fontWeight: 700 }}
                    disabled={formSaving}
                  >
                    {formSaving ? 'Speichern...' : 'Veröffentlichen'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default News;
